/**
 * Outcome trading fees (#777): the one place that models what Hyperliquid charges on HIP-4 outcome trades, used by the
 * /learn fee breakdown (and by the order ticket once it shows fees). Sources: docs/research/EDUCATION_SOURCES.md.
 *
 * Facts (Hyperliquid docs, trading/fees "Outcome tokens", cross-checked on live fills 2026-10-18,
 * docs/research/samples/s777-fees-live.txt):
 *   - Hyperliquid fees are charged only when CLOSING (selling) or SETTLING, never when opening (buying): 0 of 12,746
 *     mainnet outcome buy fills paid a fee;
 *   - a sell pays the account's SPOT rate: the taker ("cross") rate when it trades immediately, the maker ("add")
 *     rate when it rested on the book. Outcomes have no maker rebates (a rebate-tier account pays 0 on maker fills);
 *   - settlement charges the winning side a fee on what it receives (`settleFraction * shares`), at the taker rate;
 *     a side that settles to $0 pays nothing;
 *   - the outcome's `deployerFeeScale` (outcomeMeta) multiplies the rate. Measured live: no scale (protocol
 *     outcomes) = x1, scale "1.0" = x2. Other scales (testnet has 0.75, 2, 3, 5, 10) follow the documented HIP-3
 *     formula (scale < 1: 1 + scale; otherwise 2 * scale), confirmed by Victor 2026-10-18 (REVIEW R4), see
 *     https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/hip-4-deployer-actions#deployer-fee-scale;
 *   - the referral discount (userFees.activeReferralDiscount) reduces the rate; the staking discount is already in
 *     userFees' spot rates;
 *   - fees are paid in the quote token (USDC).
 * Builder fee (#770): charged on outcome BUYS AND SELLS (never on settlement, which is not an order), at the builder's
 *   rate on the order value. Live-verified on testnet (docs/research/BUILDER_CODES.md, samples/s770-builder-testnet.txt:
 *   f=100 charged 0.00128 on a 1.28 USDC buy and 0.0012 on a 1.2 USDC sell); the Hyperliquid docs say spot builder fees
 *   apply to sells only, which is not what happens for outcomes. The maths is builder/builder.ts builderFeeAtRate, the
 *   same function the order ticket uses (builderFeeUsd). BUILDER_FEE_RATE = 0: builder codes are off by default; the
 *   Learn page passes the configured builder's rate (builderConfigFor) when one is set.
 */
import { builderFeeAtRate } from '../builder/builder';
import { add, cmp, decToString, mul, ONE, parseDec, roundDec, sub, ZERO, type Dec } from './decimal';

/** Hyperliquid spot fee schedule, base tier (14-day volume under $5M, no staking discount): taker 0.070%. */
export const SPOT_BASE_TAKER_RATE = '0.0007';
/** Base-tier spot maker rate: 0.040%. */
export const SPOT_BASE_MAKER_RATE = '0.0004';

/**
 * Default builder fee as a fraction of the order value (#770): 0 = builder codes off (the default). Charged on buys and
 * sells alike. Hyperliquid caps outcome (spot) builder fees at 1% and the user must approve a maximum first.
 */
export const BUILDER_FEE_RATE = '0';

/** What happens to the shares: buying opens, selling closes, settlement pays out. */
export type OutcomeFeeEvent = 'buy' | 'sell' | 'settle';

export interface DeployerMultiplier {
  /** Factor applied to the base rate, e.g. "2". */
  factor: string;
  /**
   * True when this scale was also measured on live fills (none, "0", "1.0"); false = the documented HIP-3 formula,
   * not measured live. Both are documented (REVIEW R4 resolved), so the UI shows no "(estimated)" label either way.
   */
  verified: boolean;
}

const dec = (v: string | number | undefined, fallback: Dec): Dec => {
  if (v === undefined || v === '') return fallback;
  const d = parseDec(v);
  return d && cmp(d, ZERO) >= 0 ? d : fallback;
};

/** Rate multiplier for an outcome's `deployerFeeScale` (missing = protocol outcome = x1). */
export function deployerMultiplier(deployerFeeScale?: string | null): DeployerMultiplier {
  const s = dec(deployerFeeScale ?? undefined, ZERO);
  const factor = cmp(s, ONE) < 0 ? add(ONE, s) : mul(s, { n: 2n, s: 0 });
  const verified = cmp(s, ZERO) === 0 || cmp(s, ONE) === 0;
  return { factor: decToString(factor), verified };
}

export interface OutcomeFeeInput {
  event: OutcomeFeeEvent;
  /**
   * USDC value the fee is measured on: price * shares for a trade, settleFraction * shares for settlement (a
   * winning binary share settles to 1, a losing one to 0).
   */
  value: string;
  /** Sell only: true when the order trades immediately (taker), false when it rested on the book (maker). */
  taker?: boolean;
  /** outcomeMeta `deployerFeeScale` of the outcome (missing = none). */
  deployerFeeScale?: string | null;
  /** The account's spot taker / maker rates (userFees userSpotCrossRate / userSpotAddRate); default base tier. */
  takerRate?: string;
  makerRate?: string;
  /** userFees activeReferralDiscount (e.g. "0.04"); default 0. */
  referralDiscount?: string;
  /** Builder fee fraction (#770), charged on buys and sells; default BUILDER_FEE_RATE (0 = off). */
  builderFeeRate?: string;
}

export interface OutcomeFee {
  /** Hyperliquid fee rate applied (fraction, e.g. "0.0014" = 0.14%). */
  rate: string;
  /** Hyperliquid fee in USDC (8 decimals max, rounded up). */
  hlFee: string;
  /** Builder fee in USDC (#770): buys and sells, 0 on settlement (8 decimals max, rounded up). */
  builderFee: string;
  /** hlFee + builderFee. */
  total: string;
  multiplier: DeployerMultiplier;
  /** Why no Hyperliquid fee applies, if none does. */
  freeReason?: 'opening' | 'settles-to-zero';
}

const FEE_DECIMALS = 8;

/** Fee for one outcome trade or settlement. Never throws: junk inputs count as 0. */
export function outcomeFee(i: OutcomeFeeInput): OutcomeFee {
  const value = dec(i.value, ZERO);
  const multiplier = deployerMultiplier(i.deployerFeeScale);
  let rate = ZERO;
  let freeReason: OutcomeFee['freeReason'];
  if (i.event === 'buy') {
    freeReason = 'opening';
  } else if (i.event === 'settle' && cmp(value, ZERO) === 0) {
    freeReason = 'settles-to-zero';
  } else {
    const useTaker = i.event === 'settle' || i.taker !== false;
    const given = useTaker ? i.takerRate : i.makerRate;
    // userFees rates can be negative (maker rebate tiers): keep the sign so the rebate clamp below applies.
    const base =
      (given !== undefined && given !== '' ? parseDec(given) : null) ??
      parseDec(useTaker ? SPOT_BASE_TAKER_RATE : SPOT_BASE_MAKER_RATE)!;
    const disc = dec(i.referralDiscount, ZERO);
    const keep = cmp(disc, ONE) >= 0 ? ZERO : sub(ONE, disc);
    // A negative (rebate) maker rate is never paid out on outcomes: clamp to 0.
    rate = cmp(base, ZERO) > 0 ? mul(mul(base, parseDec(multiplier.factor)!), keep) : ZERO;
  }
  const hlFee = roundDec(mul(value, rate), FEE_DECIMALS, 'up');
  // Builder fee: every order (buy or sell), never settlement. Same maths as the order ticket (builder/builder.ts).
  const builderFee =
    i.event === 'settle' ? ZERO : parseDec(builderFeeAtRate(decToString(value), i.builderFeeRate ?? BUILDER_FEE_RATE))!;
  return {
    rate: decToString(rate),
    hlFee: decToString(hlFee),
    builderFee: decToString(builderFee),
    total: decToString(add(hlFee, builderFee)),
    multiplier,
    ...(freeReason ? { freeReason } : {}),
  };
}

/** Fraction "0.0014" -> percent "0.14" (exact). */
export function rateToPct(rate: string): string {
  const d = parseDec(rate);
  return d ? decToString(mul(d, { n: 100n, s: 0 })) : '0';
}
